'use client';

import { useRouter } from 'next/navigation';

import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { useQueryClient } from '@tanstack/react-query';

import classNames from 'classnames';

import { Spinner } from '@uikit';

import type { HotOffersFilterValue } from '@api';

import { MIXPANEL_EVENT_NAMES } from '@shared/api/mixPanel';
import type { HotOffer, HotOfferSortByFilter } from '@shared/api/types/hotOffers';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { useTrackMixPanel } from '@shared/common/providers/MixPanelProvider';

import { useFeatureTogglesQuery } from '@query/featureToggles';
import { useHotOffersInfinityQuery } from '@query/hotOffers';

import PageHeader from '@store:web/components/layout/PageHeader/PageHeader';
import HotOfferCardLite from '@store:web/components/pages/HotOffersPage/HotOfferCardLite';
import HotOffersFilters from '@store:web/components/pages/HotOffersPage/HotOffersFilters';

export interface ClientHotOffersPageProps {
  mode?: HotOfferSortByFilter;
}

export function ClientHotOffersPage({ mode }: ClientHotOffersPageProps) {
  const { user } = useAuth();

  useTrackMixPanel(MIXPANEL_EVENT_NAMES.HOT_OFFERS_PAGE_VIEW, {
    distinct_id: user?.id,
  });

  const router = useRouter();
  const [filterBy, setFilterBy] = useState<HotOfferSortByFilter>(() => mode ?? 'all');
  const { data: { binanceHotOffers } = {} } = useFeatureTogglesQuery();
  const {
    data: { pages = [] } = {},
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
  } = useHotOffersInfinityQuery({
    mode: filterBy,
  });
  const { ref } = useInView({
    onChange: (inView) =>
      inView && !isError && !isLoading && !isFetchingNextPage && fetchNextPage(),
  });

  const handleFilterChange = (value: HotOffersFilterValue) => {
    const updatedSearchParams = new URLSearchParams({ mode: value });

    router.replace(`/hot-offers?${updatedSearchParams}`);

    setFilterBy(value);
  };

  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ['infiniteHotOffersList', 'mode', filterBy ?? 'all'],
    });
  }, [filterBy, queryClient]);

  useEffect(() => {
    setFilterBy(mode ?? 'all');
  }, [mode]);

  const hotOffers: HotOffer[] = useMemo(() => pages.flatMap((page) => page.data ?? []), [pages]);

  useEffect(() => {
    history.scrollRestoration = 'manual';
  }, []);

  return (
    <main className="w-full flex flex-grow flex-col items-center bg-neutral-50">
      <PageHeader page="hot-offers" />

      <div className="v2-container pt-1 w-full flex flex-col">
        {binanceHotOffers && (
          <HotOffersFilters filterBy={filterBy} onFilterByChange={handleFilterChange} />
        )}

        {!!hotOffers.length && (
          <div
            id="onboarding-hot-offers"
            className={classNames(`w-full grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3`)}
          >
            {hotOffers.map((hotOffer, i) => (
              <HotOfferCardLite key={hotOffer.id} imagePriority={i <= 2} offer={hotOffer} />
            ))}
          </div>
        )}
      </div>

      <div className="w-full flex flex-col flex-grow flex-shrink-0 items-center justify-center gap-2 py-6">
        {(isLoading || hasNextPage) && (
          <>
            {isError && (
              <>
                <span className="text-neutral-500">Couldn&apos;t load more offers</span>
                <button onClick={() => fetchNextPage()} className="button button-outline-secondary">
                  Try Again
                </button>
              </>
            )}

            {!isError && (
              // Key is added to trigger in view cb again if component is already in view
              <Spinner key={pages.length} ref={ref} />
            )}
          </>
        )}

        {!isLoading && !hasNextPage && (
          <span className="text-neutral-500">
            {isError && "Couldn't load more offers"}
            {!hasNextPage && !!pages.length && "That's all for now!"}
            {!hasNextPage && !isError && !pages.length && 'No offers available at the moment.'}
          </span>
        )}
      </div>
    </main>
  );
}
